import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Container,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  useMediaQuery,
  Fade
} from '@mui/material';
import {
  Close,
  Article,
  CalendarToday
} from '@mui/icons-material';
import { safeFormatDate, formatDateRange } from '../utils/dateUtils';
import backendUrl from '../store/config';
import { useAppSelector } from '../store/hooks';
import EnhancedMarkdownRenderer from '../components/EnhancedMarkdownRenderer';

interface Summary {
  id: string;
  title: string;
  category: string;
  content: string;
  created_at: string;
  status: string;
  visible_to_users: boolean;
  // Time range fields
  start_date?: string;
  end_date?: string;
  date_range_display?: string;
  date_range?: string; // Optional human-readable date range
  source?: string; // Knowledge base source
}

const SOURCE_LABELS: { [key: string]: string } = {
  starch: 'Starch',
  non_food: 'Non-Food',
  sns: 'SNS',
};

const VisibleSummaries: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const token = useAppSelector((state: any) => state.auth.token);
  
  const [summaries, setSummaries] = useState<Summary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSummary, setSelectedSummary] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchVisibleSummaries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/summaries/visible`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSummaries(data);
        setError(null);
      } else {
        throw new Error('Failed to fetch summaries');
      }
    } catch (error) {
      console.error('Error fetching visible summaries:', error);
      setError('Failed to load summaries. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchVisibleSummaries();
  }, [fetchVisibleSummaries]);

  const handleSummaryClick = (summary: Summary) => {
    setSelectedSummary(summary);
  };

  const handleCloseDialog = () => {
    setSelectedSummary(null);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Button variant="contained" onClick={fetchVisibleSummaries} sx={{ mt: 2 }}>
            Try Again
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Fade in={true}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 600, mb: 2 }}>
            Available Summaries
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
            Browse through summaries that have been made available for viewing. Click on any summary to read the full content.
          </Typography>
        </Box>

        {/* Summaries Grid */}
        {summaries.length > 0 ? (
          <Grid container spacing={3}>
            {summaries.map((summary) => (
              <Grid item xs={12} sm={6} md={4} key={summary.id}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8]
                    }
                  }}
                  onClick={() => handleSummaryClick(summary)}
                >
                  <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Header with icon */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Article sx={{ color: 'primary.main', mr: 1 }} />
                    </Box>

                    {/* Title */}
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, lineHeight: 1.3 }}>
                      {summary.title}
                    </Typography>

                    {/* Source badge */}
                    {summary.source && summary.source !== 'starch' && (
                      <Chip
                        label={SOURCE_LABELS[summary.source] || summary.source}
                        size="small"
                        sx={{
                          mb: 1,
                          alignSelf: 'flex-start',
                          backgroundColor: '#a31830',
                          color: '#fff',
                          fontSize: '0.65rem',
                          height: 20,
                        }}
                      />
                    )}

                    {/* Category */}
                    {summary.category && (
                      <Chip
                        label={summary.category.replace('_', ' ')}
                        size="small"
                        sx={{ mb: 2, alignSelf: 'flex-start' }}
                      />
                    )}

                    {/* Date Range */}
                    {summary.date_range_display && (
                      <Chip
                        icon={<CalendarToday />}
                        label={summary.date_range_display}
                        size="small"
                        sx={{ mb: 2, alignSelf: 'flex-start' }}
                      />
                    )}

                    {/* Content preview */}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary', 
                        mb: 2, 
                        flex: 1,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {summary.content.substring(0, 150)}...
                    </Typography>

                    {/* Date and optional date range */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', color: 'text.disabled', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarToday sx={{ fontSize: 14, mr: 0.5 }} />
                        <Typography variant="caption">
                          {safeFormatDate(summary.created_at, 'MMM dd, yyyy')}
                        </Typography>
                      </Box>
                      {summary.date_range && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Date range: {formatDateRange(summary.date_range)}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" sx={{ color: 'text.secondary', mb: 2 }}>
              No Summaries Available
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.disabled' }}>
              There are currently no summaries available for viewing.
            </Typography>
          </Box>
        )}

        {/* Summary Detail Dialog */}
        <Dialog
          open={!!selectedSummary}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          {selectedSummary && (
            <>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedSummary.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {safeFormatDate(selectedSummary.created_at, 'MMM dd, yyyy HH:mm')}
                  </Typography>
                  {selectedSummary.date_range && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Date range: {formatDateRange(selectedSummary.date_range)}
                    </Typography>
                  )}
                </Box>
                <IconButton onClick={handleCloseDialog}>
                  <Close />
                </IconButton>
              </DialogTitle>
              
              <DialogContent dividers>
                <EnhancedMarkdownRenderer 
                  content={selectedSummary.content} 
                  variant="dialog"
                />
              </DialogContent>
              
              <DialogActions>
                <Button onClick={handleCloseDialog} variant="contained">
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </Fade>
  );
};

export default VisibleSummaries;